<h4>About the Organ Transplantation System</h4>
<p>
  This infographic provides an overview of the national organ transplantation system
  <a href="https://www.organdonor.gov/sites/default/files/organ-donor/professional/materials/Transplant-System-Infographic.pdf" rel="noopener noreferrer" target="_blank" class="usa-link usa-link--external">View/Download</a>
  (PDF - 67 KB).
</p>
<div class="usa-accordion">
  <h4 class="usa-accordion__heading">
    <button
      type="button"
      class="usa-accordion__button"
      aria-expanded="false"
      aria-controls="what-is-an-organ-transplant-center"
    >
      What is an Organ Transplant Center?
    </button>
  </h4>
  <div id="what-is-an-organ-transplant-center" class="usa-accordion__content" hidden>
    <p>
      An organ transplant center is a hospital that registers patients on the national waiting list and performs organ
      transplants. The transplant team at the transplant center evaluates each person in need of an organ before
      transplant. The team includes:
    </p>
    <ul class="padding-left-4">
      <li>Doctors</li>
      <li>Nurse practitioners</li>
      <li>Surgeons</li>
      <li>Transplant coordinators</li>
      <li>Social workers</li>
      <li>Financial counselors</li>
      <li>Nutritionists</li>
      <li>Neuropsychologists</li>
      <li>Pharmacy specialists</li>
    </ul>
    <p>
      This specialized team works with patients throughout the transplant process. Transplant centers are committed to
      delivering the highest quality and safest healthcare to patients. Within a transplant center, there are transplant
      program(s) focusing on a particular organ such as kidney. As such a transplant center can have more than one
      transplant program.
    </p>
  </div>
</div>

<div class="usa-accordion">
  <h4 class="usa-accordion__heading">
    <button
      type="button"
      class="usa-accordion__button"
      aria-expanded="false"
      aria-controls="what-organs-can-be-transplanted"
    >
      What organs can be transplanted?
    </button>
  </h4>
  <div id="what-organs-can-be-transplanted" class="usa-accordion__content" hidden>
    <p>Transplanted organs can come from both deceased and living donors.</p>
    <p>
      Organs from deceased organ donors include: kidneys (2), liver, lungs (2), heart, pancreas, and intestines. In
      2014,
      <a href="https://www.organdonor.gov/learn/what-can-be-donated/face-hands" rel="noopener noreferrer" class="usa-link usa-link--external" target="_blank" title="hands and faces"
        >hands and faces</a
      >
      were added to the organ transplant list.
    </p>
    <p>
      Organs from living organ donors include: one kidney, a lung, or a portion of the liver, pancreas, or intestine.
    </p>
  </div>
</div>

<div class="usa-accordion">
  <h4 class="usa-accordion__heading">
    <button
      type="button"
      class="usa-accordion__button"
      aria-expanded="false"
      aria-controls="what-is-the-scientific-registry"
    >
      What is the Scientific Registry of Transplant Recipient (SRTR)?
    </button>
  </h4>
  <div id="what-is-the-scientific-registry" class="usa-accordion__content" hidden>
    <p>
      The SRTR is responsible for providing statistical and other analytic support to the OPTN for purposes including
      the formulation and evaluation of organ allocation and other OPTN policies. Additionally, SRTR provides analytic
      support to HHS in a variety of areas including: policy formulation and evaluation, system performance metrics,
      economic analysis, and preparation of recurring and special reports to Congress.
    </p>
    <p>
      Section 373 of the Public Health Service (PHS) Act requires the operation of SRTR to support ongoing evaluation of
      the scientific and clinical status of solid organ transplantation. The U.S. Congress passed the National Organ
      Transplant Act (NOTA; P.L. 98-507) in 1984.
    </p>
  </div>
</div>

<div class="usa-accordion padding-bottom-4">
  <h4 class="usa-accordion__heading">
    <button
      type="button"
      class="usa-accordion__button"
      aria-expanded="false"
      aria-controls="what-is-organ-procurement"
    >
      What is the Organ Procurement &amp; Transplantation Network (OPTN)?
    </button>
  </h4>
  <div id="what-is-organ-procurement" class="usa-accordion__content" hidden>
    <p>
      The Organ Procurement and Transplantation Network (OPTN) is a unique public-private partnership that links all
      professionals involved in the U.S. donation and transplantation system. Also crucial to the system are registered
      organ donors, people who comment on policy proposals and countless volunteers who support donation and
      transplantation, among many others.
    </p>
    <p>
      <a
        href="http://uscode.house.gov/view.xhtml?hl=false&amp;edition=prelim&amp;req=granuleid%3AUSC-2014-title42-section274&amp;num=0"
        target="_blank"
        class="usa-link usa-link--external"
        rel="noopener noreferrer"
        >National Organ Transplant Act</a
      >
      (NOTA) established the OPTN in 1984. In 2000, The U.S. Department of Health and Human Services (HHS) implemented a
      final rule establishing a regulatory framework for the structure and operations of the OPTN.
    </p>
    <p>
      With all of OPTN's collective efforts focused on patients, the
      <a
        href="https://www.hrsa.gov/optn/about"
        target="_blank"
        class="usa-link usa-link--external"
        rel="noopener noreferrer"
        >goals of the OPTN</a
      >
      are to increase the number of and access to transplants, improve survival rates after transplantation, and to
      promote patient safety and efficient management of the system.
    </p>
    <p>
      The OPTN acts through its board of directors and committees, who bring a wealth of commitment and technical
      knowledge to guide us. Committees address issues of concern in the transplant community. The board establishes and
      maintains
      <a href="https://www.hrsa.gov/optn/policies-bylaws/policies" rel="noopener noreferrer" target="_blank" class="usa-link usa-link--external"
        >transplant policies</a
      >
      (operational rules) and
      <a href="https://www.hrsa.gov/optn/policies-bylaws/bylaws" rel="noopener noreferrer" target="_blank" class="usa-link usa-link--external">bylaws</a>
      (membership requirements) that govern the OPTN.
    </p>
    <p>
      OPTN Patient Services Line (Operated by United Network for Organ Sharing) is available at 888-894-6361,
      Monday-Friday from 8:30 a.m. to 5:30 p.m. ET.
    </p>
  </div>
</div>
<h4 class="">Learn More</h4>
<ul class="padding-left-4">
  <li>
    Organ donation and registration:
    <a href="https://organdonor.gov/" rel="noopener noreferrer" target="_blank" title="organdonor.gov" class="usa-link usa-link--external">organdonor.gov</a>
    |
    <a href="https://donaciondeorganos.gov/" rel="noopener noreferrer" target="_blank" title="donaciondeorganos.gov" class="usa-link usa-link--external">donaciondeorganos.gov</a>
    |
    <a
      class="usa-link usa-link--external"
      href="https://www.facebook.com/organdonor.gov"
      target="_blank"
      rel="noopener noreferrer"
      title="facebook.com/organdonor.gov"
    >
      facebook.com/organdonor.gov
    </a>
    |
    <a
      class="usa-link usa-link--external"
      href="https://www.facebook.com/USdonaciondeorganos.gov/"
      target="_blank"
      rel="noopener noreferrer"
      title="facebook.com/Usdonaciondeorganos.gov"
    >
      facebook.com/Usdonaciondeorganos.gov
      </a>
  </li>
  <li>
    Matching and Policies on Organ Transplantation:
    <a class="usa-link usa-link--external" href="https://www.hrsa.gov/optn" rel="noopener noreferrer" target="_blank">
      www.hrsa.gov/optn</a>
  </li>
  <li>
    Public Information on Organ Transplantation:
    <a class="usa-link usa-link--external" href="https://srtr.hrsa.gov/" rel="noopener noreferrer" target="_blank">
      srtr.hrsa.gov
    </a>
  </li>
  <li>
    National Living Donor Assistance Center:
    <a
      class="usa-link usa-link--external"
      href="https://www.livingdonorassistance.org"
      target="_blank"
      rel="noopener noreferrer"
    >
      livingdonorassistance.org
    </a>
  </li>
</ul>
<p>
  The Division of Transplantation (DoT) within the
  <a href="https://www.hrsa.gov/about/organization/bureaus/hsb" rel="noopener noreferrer" target="_blank" class="usa-link usa-link--external" 
    >Health Systems Bureau (HSB)</a
  >
  is the primary federal entity responsible for oversight of the organ and blood stem cell (bone marrow and umbilical
  cord blood) transplant systems in the U.S. DoT also supports efforts to increase the supply of deceased donor organs
  and blood stem cells made available for transplant and ensure the safety of living organ donation.
</p>
